import { useCallback, useEffect, useRef } from 'react'
import { useHistory, useRouteMatch } from '@fs/zion-router'

export const MTM_BASE_PATH = '/mytimemachine'
export const MTM_TIMELINE_SEGMENT = 'timeline'
// The phone page a kiosk's Add from your phone QR code opens (DSC-10420).
export const MTM_PHONE_ADD_PATH = `${MTM_BASE_PATH}/add`

// The fan chart, the lower arc and the timeline each describe a person with a
// slightly different key, so resolve the identifier the same way the fan chart
// service does (see services/fanchartDescendants.js).
export const getMtmPersonId = (person) => person?.personId ?? person?.id ?? person?.pid ?? null

// decodeURIComponent throws on malformed input such as a truncated '%' escape, and
// the decode happens during render, so an unguarded call would take the whole
// experience down via the error boundary. Falling back to the raw value is safe: it
// cannot match anyone in the tree, so it lands in the unknown-id branch below and the
// URL gets rewritten back to the selector.
const decodePersonId = (value) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Keeps the open timeline in sync with the URL so the browser back button walks
 * back through the experience instead of leaving it.
 *
 * Without this the whole experience lives at a single path, so back pops the one
 * history entry there is and drops the visitor on the landing page (DSC-9666).
 *
 * The selector is the base path and each person's timeline is a nested path, so
 * opening a timeline pushes an entry that back can pop. The transient animation
 * phases deliberately stay out of the URL — only the two real destinations are
 * addressable.
 *
 * Signed-out visitors go straight to the timeline and never see the selector, so
 * there is nothing to navigate between and the sync stays switched off for them.
 */
export default function useMtmTimelineRoute({
  findPersonById,
  isExitingTimeline,
  isTimelineViewOpen,
  isTreeLoading,
  onRouteEnterTimeline,
  onRouteExitTimeline,
  onRoutePersonUnknown,
  selectedPersonId,
  signedIn,
}) {
  const history = useHistory()
  const { path: matchedPath } = useRouteMatch()
  // Drop any trailing slash so appending the timeline segment cannot double up. A
  // root match collapses to '' here, which is why the selector path re-adds the '/'.
  const basePath = matchedPath.replace(/\/$/, '')
  const selectorPath = basePath || '/'
  const timelineMatch = useRouteMatch(`${basePath}/${MTM_TIMELINE_SEGMENT}/:mtmPersonId`)
  const rawRoutePersonId = timelineMatch?.params?.mtmPersonId ?? null
  const routePersonId = rawRoutePersonId ? decodePersonId(rawRoutePersonId) : null

  // The lookup closes over the ancestor tree, which is rebuilt on most renders. Held
  // in a ref so the sync below re-runs when the data actually changes rather than
  // every time that closure is recreated.
  const findPersonByIdRef = useRef(findPersonById)
  findPersonByIdRef.current = findPersonById

  const navigateToTimeline = useCallback(
    (person) => {
      const nextPersonId = getMtmPersonId(person)
      if (!nextPersonId) return
      history.push(`${basePath}/${MTM_TIMELINE_SEGMENT}/${encodeURIComponent(nextPersonId)}`)
    },
    [basePath, history]
  )

  const navigateToSelector = useCallback(() => {
    history.push(selectorPath)
  }, [history, selectorPath])

  // Used when a claimed unknown id fails to resolve (see below): the timeline
  // segment is dropped without adding a history entry, exactly like the
  // built-in unknown-id branch.
  const replaceWithSelector = useCallback(() => {
    history.replace(selectorPath)
  }, [history, selectorPath])

  // Back (or any other move off the timeline path) runs the same close the in-app
  // back control does, so the exit animation and the event overlay behave the same
  // either way. Skipping while already exiting keeps that animation from being cut
  // short by a second close.
  useEffect(() => {
    if (!signedIn) return
    if (routePersonId) return
    if (!isTimelineViewOpen || isExitingTimeline) return

    onRouteExitTimeline?.()
  }, [isExitingTimeline, isTimelineViewOpen, onRouteExitTimeline, routePersonId, signedIn])

  // The reverse trip: a cold deep link, a forward button, or a back that lands on a
  // different person's timeline. The ancestor tree is what resolves an id into a
  // person, so nothing can happen until it has loaded.
  useEffect(() => {
    if (!signedIn) return
    if (!routePersonId || isTreeLoading) return
    if (isTimelineViewOpen && routePersonId === selectedPersonId) return

    const person = findPersonByIdRef.current?.(routePersonId)

    // An id that is not in this visitor's tree can still name a real tree person
    // (a sibling or spouse selected from the relationships panel), so the caller
    // gets a chance to claim it — returning true means "I'm resolving this id
    // asynchronously, leave the url alone". Unclaimed ids drop the segment
    // without adding history, as before.
    if (!person) {
      if (onRoutePersonUnknown?.(routePersonId)) return

      history.replace(selectorPath)
      return
    }

    onRouteEnterTimeline?.(person)
  }, [
    history,
    isTimelineViewOpen,
    isTreeLoading,
    onRouteEnterTimeline,
    onRoutePersonUnknown,
    routePersonId,
    selectedPersonId,
    selectorPath,
    signedIn,
  ])

  return { routePersonId, navigateToTimeline, navigateToSelector, replaceWithSelector }
}
